<script lang="ts" setup>
  import { storeToRefs } from 'pinia';
  import type { Ref } from 'vue';
  import { inject, ref } from 'vue';

  import { EnumRoutes } from '@/helpers/enums/routes.enum';
  import { useI18 } from '@/plugins';
  import { useNotificationsStore } from '@/stores/notifications.store';
  import NotificationList, {
    type INotificationList,
  } from '@/submodules/himam-common-components/src/components/notification/NotificationList.vue';
  import NotificationsSettings from '@/submodules/himam-common-components/src/components/notification/NotificationsSettings.vue';
  const notificationsStore = useNotificationsStore();
  const { unreadCount } = storeToRefs(notificationsStore);
  const { markAllAsRead } = notificationsStore;
  const showNotificationsList = ref(false);

  const { t } = useI18();

  enum tabValue {
    all = 'all',
    unread = 'unread',
    settings = 'settings',
  }

  const tab = ref(tabValue.all);

  const notificationsList: Ref<INotificationList[]> = inject(
    'notificationsList',
    ref([])
  );

  const unReadNotificationsList: Ref<INotificationList[]> = inject(
    'unReadNotificationsList',
    ref([])
  );
</script>

<template>
  <v-menu
    v-model="showNotificationsList"
    :open-on-click="true"
    :scrim="true"
    transition="slide-y-transition"
  >
    <template #activator="{ props }">
      <div class="position-relative" v-bind="props">
        <svg
          width="18"
          height="21"
          viewBox="0 0 18 21"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M7 19H11C11 20.1 10.1 21 9 21C7.9 21 7 20.1 7 19ZM18 17V18H0V17L2 15V9C2 5.9 4 3.2 7 2.3V2C7 0.9 7.9 0 9 0C10.1 0 11 0.9 11 2V2.3C14 3.2 16 5.9 16 9V15L18 17ZM14 9C14 6.2 11.8 4 9 4C6.2 4 4 6.2 4 9V16H14V9Z"
            class="fill-icon"
          />
        </svg>
        <div
          v-if="unreadCount"
          class="cart-count d-flex align-center justify-center"
        >
          {{ unreadCount }}
        </div>
      </div>
    </template>

    <v-sheet class="rounded-lg" width="350">
      <v-toolbar color="transparent" density="compact">
        <v-toolbar-title class="text-subtitle-1 text-primary" @click.stop>
          {{ t('menu.notifications') }}
        </v-toolbar-title>
        <v-toolbar-items v-if="unreadCount > 0" variant="text">
          <v-btn class="text-caption text-text" @click="markAllAsRead">
            {{ t('shared.markAllAsRead') }}
          </v-btn>
        </v-toolbar-items>
      </v-toolbar>

      <v-tabs
        v-model="tab"
        :fixed-tabs="false"
        :grow="false"
        :style="'color: #808080'"
        class="text-capitalize"
        color="primary"
        density="compact"
        slider-color="primary"
      >
        <v-row class="align-center" @click.stop>
          <v-tab
            :value="tabValue.all"
            class="text-capitalize text-decoration-none"
          >
            {{ t('shared.all') }}
          </v-tab>
          <v-divider class="my-4 mx-2" color="primary" vertical></v-divider>
          <!-- Unread Tab -->
          <v-badge :color="'primary'" :content="unreadCount" inline>
            <v-tab
              :value="tabValue.unread"
              class="text-capitalize text-decoration-none"
            >
              {{ t('shared.unRead') }}
            </v-tab>
          </v-badge>
          <v-spacer></v-spacer>

          <v-btn-text
            v-model="tab"
            icon
            :ripple="false"
            :color="tab == tabValue.settings ? 'primary' : 'text'"
            class="me-2"
            :value="tabValue.settings"
            @click="() => (tab = tabValue.settings)"
          >
            <v-icon icon="mdi-cog-outline" />
          </v-btn-text>
        </v-row>
      </v-tabs>

      <v-divider class="mb-2" @click.stop></v-divider>

      <v-window v-model="tab">
        <v-window-item :value="tabValue.all">
          <NotificationList :notifications-list="notificationsList" />
        </v-window-item>
        <v-window-item :value="tabValue.unread">
          <NotificationList :notifications-list="unReadNotificationsList" />
        </v-window-item>
        <v-window-item :value="tabValue.settings" @click.stop>
          <NotificationsSettings />
        </v-window-item>
      </v-window>

      <div v-if="tab != tabValue.settings" class="d-flex justify-end">
        <v-btn
          :to="{
            name: EnumRoutes.misc.notifications.name,
          }"
          class="text-caption text-decoration-underline text-text"
          variant="text"
          @click.prevent="showNotificationsList = false"
        >
          {{ t('shared.seeAllNotifications') }}
        </v-btn>
      </div>
    </v-sheet>
    <slot name="notificationList" />
  </v-menu>
</template>
